import { lazy, Suspense, type ComponentType } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router'

import { FullPageSpinner } from '@/components/common/full-page-spinner'
import { RequireAuth } from '@/features/auth/components/require-auth'

/**
 * `lazy`, but resilient to a deploy that happened while the tab was open.
 *
 * Each deploy emits fresh content-hashed chunks and deletes the old ones.
 * A tab loaded before the deploy still references the OLD chunk names, so
 * the first navigation to a not-yet-loaded route tries to fetch a file
 * that no longer exists and throws "Failed to fetch dynamically imported
 * module" — a dead end that only a manual refresh escaped.
 *
 * On that specific failure we reload the page ONCE (guarded by a
 * sessionStorage flag keyed to the chunk, so a genuinely missing module
 * cannot loop). The reload pulls the new index.html and its new chunk
 * names, and the navigation succeeds.
 */
function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  key: string
) {
  return lazy(async () => {
    try {
      return await factory()
    } catch (error) {
      const flag = `chunk-reload:${key}`
      if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, '1')
        window.location.reload()
        // Never resolves; the reload replaces the page.
        return await new Promise<{ default: T }>(() => {})
      }
      throw error
    }
  })
}

/**
 * Routes.
 *
 * Every feature area is lazy-loaded. The tree renderer, the media
 * gallery and the story editor are each large and each used by a
 * different subset of people on a given visit; loading all three to show
 * a sign-in form is exactly the mobile-first failure we are trying to
 * avoid.
 *
 * URLs are family-scoped (`/f/:familySlug/...`). Putting the tenant in
 * the path rather than in a global "current family" variable means links
 * are shareable, the back button works, and two tabs can show two
 * different family archives.
 */

const SignInPage = lazyWithReload(() => import('@/features/auth/pages/sign-in-page'), 'sign-in')
const AuthCallbackPage = lazyWithReload(() => import('@/features/auth/pages/auth-callback-page'), 'auth-callback')
const FamiliesPage = lazyWithReload(() => import('@/features/families/pages/families-page'), 'families')
const FamilyDashboardPage = lazyWithReload(() => import('@/features/families/pages/family-dashboard-page'), 'dashboard')
const PeopleListPage = lazyWithReload(() => import('@/features/people/pages/people-list-page'), 'people-list')
const PersonProfilePage = lazyWithReload(() => import('@/features/people/pages/person-profile-page'), 'person-profile')
const PlacesListPage = lazyWithReload(() => import('@/features/places/pages/places-list-page'), 'places-list')
const PlaceProfilePage = lazyWithReload(() => import('@/features/places/pages/place-profile-page'), 'place-profile')
const ReconcilePlacesPage = lazyWithReload(() => import('@/features/places/pages/reconcile-places-page'), 'reconcile-places')
const PlaceRedirectPage = lazyWithReload(() => import('@/features/places/pages/place-redirect-page'), 'place-redirect')
const ArchiveListPage = lazyWithReload(() => import('@/features/archive/pages/archive-list-page'), 'archive-list')
const ArchiveItemPage = lazyWithReload(() => import('@/features/archive/pages/archive-item-page'), 'archive-item')
const SourcesListPage = lazyWithReload(() => import('@/features/sources/pages/sources-list-page'), 'sources-list')
const SourceProfilePage = lazyWithReload(() => import('@/features/sources/pages/source-profile-page'), 'source-profile')
const TreePage = lazyWithReload(() => import('@/features/tree/pages/tree-page'), 'tree')
const MembersPage = lazyWithReload(() => import('@/features/members/pages/members-page'), 'members')
const AcceptInvitationPage = lazyWithReload(() => import('@/features/members/pages/accept-invitation-page'), 'accept-invitation')
const OnboardingWizardPage = lazyWithReload(() => import('@/features/onboarding/pages/onboarding-wizard-page'), 'onboarding')
const NotFoundPage = lazyWithReload(() => import('@/app/not-found-page'), 'not-found')

const AppShell = lazyWithReload(() => import('@/app/app-shell'), 'app-shell')

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/auth/callback', element: <AuthCallbackPage /> },
  // Public: a brand-new relative reaches this from an invitation link
  // before they have any membership, and it carries its own sign-up.
  { path: '/join', element: <AcceptInvitationPage /> },

  {
    element: <RequireAuth />,
    children: [
      { path: '/', element: <FamiliesPage /> },

      // The permanent, family-neutral place URL. Resolves the archive
      // through RLS and redirects to the canonical route below, so a
      // citation written today still opens in 2050.
      { path: '/places/:placeId', element: <PlaceRedirectPage /> },

      {
        path: '/f/:familySlug',
        element: <AppShell />,
        children: [
          { index: true, element: <FamilyDashboardPage /> },
          { path: 'people', element: <PeopleListPage /> },
          { path: 'people/:personId', element: <PersonProfilePage /> },
          // `reconcile` before `:placeId` — otherwise it is read as an id.
          { path: 'places', element: <PlacesListPage /> },
          { path: 'places/reconcile', element: <ReconcilePlacesPage /> },
          { path: 'places/:placeId', element: <PlaceProfilePage /> },
          { path: 'archive', element: <ArchiveListPage /> },
          { path: 'archive/:mediaId', element: <ArchiveItemPage /> },
          { path: 'sources', element: <SourcesListPage /> },
          { path: 'sources/:sourceId', element: <SourceProfilePage /> },
          // The focus person and depth live in the query string, so a
          // particular view of the tree is a shareable link.
          { path: 'tree', element: <TreePage /> },
          { path: 'members', element: <MembersPage /> },
          // The guided join, reached right after accepting an open invite.
          { path: 'welcome', element: <OnboardingWizardPage /> },
          // Later milestones mount here: stories, research, admin.
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
])

export function AppRouter() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  )
}
